import { Component } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { NgIf, NgStyle } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { TableModule } from 'primeng/table';

@Component({
  selector: 'app-administer-med-dialog',
  templateUrl: './administer-med-dialog.component.html',
  styleUrls: ['./administer-med-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonDirective,
    NgStyle,
    EmrInputComponent,
    TableModule,
  ],
})
export class AdministerMedDialogComponent {
  // Variables to store data coming in from the caller.
  patient!: Patient;
  order!: ProviderOrder;

  orderAdministered = false;

  // In the dialog, these are fields that can be filled out.
  medicationIdFormControl: UntypedFormControl = new UntypedFormControl('');
  // These two fields show up only for Insulin-type drugs.
  doseGivenFormControl: UntypedFormControl = new UntypedFormControl('');
  bloodSugarFormControl: UntypedFormControl = new UntypedFormControl('');

  medicationIdSubscription: Subscription;

  // Variables to handle error messages
  submitted = false;
  error = '';

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {
    // Set submitted to false whenever the form's value changes;
    // this gets rid of any error messages when the user starts typing
    this.medicationIdSubscription = this.medicationIdFormControl.valueChanges.subscribe(() => {
      this.submitted = false;
    });

    this.patient = config.data.patient;
    this.order = config.data.order;
  }

  /**
   * Closes the dialog and sends medication data to the parent component.
   */
  onCloseClick(): void {
    // Pass the med back in case the user partially administered the medication
    this.medicationIdFormControl.reset();
    this.ref.close({
      order: this.order,
      administered: this.orderAdministered,
      bloodSugar: this.bloodSugarFormControl.value,
      doseGiven: this.doseGivenFormControl.value,
    });
  }

  /**
   * Submit the form and checks to see correct medication is administered
   */
  onSubmit(): void {
    if (this.correctMedId) {
      this.order.quantityGiven += 1;
      this.medicationIdFormControl.reset();
      this.orderAdministered = true;
    } else {
      this.submitted = true;
      this.error = this.getError();
    }
  }

  /**
   * Returns various error messages to be displayed on client side.
   * The error messages are checked below in descending precedence.
   */
  getError(): string {
    if (!this.correctMedId) {
      return 'This is the incorrect medication to administer!';
    }
    return 'Something went wrong - try again.';
  }

  get correctMedId(): boolean {
    const val = this.medicationIdFormControl.value as string;
    return val != null && val.trim() === this.order.medId;
  }

  /**
   * Returns whether the administered quantity is greater than the
   * provider order quantity.
   */
  get isOverdosed(): boolean {
    return this.order.quantityGiven > this.order.quantity;
  }
}
